import React from "react";
import { useRouter } from "next/router";
import { RightOutlined } from "@ant-design/icons";
import {
  faHouse,
  faFileContract,
  faScreenUsers,
} from "@fortawesome/pro-duotone-svg-icons";
import { faArrowLeft } from "@fortawesome/pro-light-svg-icons";
import { faChartArea } from "@fortawesome/free-solid-svg-icons";
import { createUseStyles } from "react-jss";

import { UIIcon } from "../ui/UIElements";
import { CandidateCheckboxFilters } from "../../common/userfuctions";

const useStyles = createUseStyles({
  wrapper: {
    display: "flex",
    alignItems: "center",
    padding: "14px 0",
  },

  breadcrumb: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 4,
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    padding: "8px 12px",
    borderRadius: 12,
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
  },

  item: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    minHeight: 28,
    padding: "4px 8px",
    borderRadius: 12,
    fontSize: 13,
    fontWeight: 600,
    color: "#6b7280",
    cursor: "pointer",
    transition: "all 0.2s ease",

    "&:hover": {
      background: "#eff6ff",
      color: "#0c64ae",
    },
  },

  activeItem: {
    background: "#eff6ff",
    color: "#0c64ae",
    fontWeight: 800,
    cursor: "default",

    "&:hover": {
      background: "#eff6ff",
      color: "#0c64ae",
    },
  },

  separator: {
    color: "#cbd5e1",
    fontSize: 11,
    margin: "0 2px",
  },

  icon: {
    fontSize: 14,
  },
});

const SelectIcons = {
  Back: faArrowLeft,
  Dashboard: faHouse,
  Jobs: faFileContract,
  Candidates: faScreenUsers,
  Home: faHouse,
  Analytics: faChartArea,
};

const capitalizeLetter = (str = "") =>
  str.charAt(0).toUpperCase() + str.slice(1);

const BItem = ({ children, active = false, onClick, disabled = false }) => {
  const styles = useStyles();

  return (
    <span
      className={`${styles.item} ${active ? styles.activeItem : ""}`}
      onClick={disabled ? undefined : onClick}
    >
      {children}
    </span>
  );
};

export const UIBreadcrumb = ({ ExtraItems = [], source = "" }) => {
  const styles = useStyles();
  const route = useRouter();

  const parseCookieData = (data) => {
    if (!data) return [];

    if (Array.isArray(data)) return data;

    if (typeof data === "string") {
      try {
        return JSON.parse(data);
      } catch {
        return data.split(",");
      }
    }

    return [];
  };

  const handleRouteUpdate = () => {
    const candidatePath = "/employer/candidates";
    const jobPath = "/employer/jobs";

    const candidateStatusData =
      CandidateCheckboxFilters?.getCandidateCheckboxFilters(
        "CandidateCheckboxFilters"
      );

    const parsedCandidateStatusFilters = parseCookieData(candidateStatusData);

    if (
      parsedCandidateStatusFilters &&
      Object.keys(parsedCandidateStatusFilters).length > 0
    ) {
      const Status = parsedCandidateStatusFilters.candidateStatusFilters || [];
      const Applicant =
        parsedCandidateStatusFilters.candidateApplicantFilters || [];
      const Query = parsedCandidateStatusFilters.jobsQueryFilter || [];
      const Source = parsedCandidateStatusFilters.sourceFilter || [];

      route.push({
        pathname: candidatePath,
        query: {
          ...route.query,
          pg: 1,
          status: Status.join(","),
          applicant: Applicant.join(","),
          jobs: Query.join(","),
          source: Source.join(","),
        },
      });
    } else {
      route.push({
        pathname: jobPath,
      });
    }
  };

  const routes =
    route.route
      ?.split("/")
      ?.filter((r) => r !== "" && r.split("[").length === 1 && r)
      ?.filter((item) => item !== "" && item !== "employer") || [];

  const items = routes.map((item) => {
    let label = capitalizeLetter(item);

    if (label === "Candidates" && source === "job") {
      label = "Jobs";
    }

    return label;
  });

  const extraItemsNormalized = (ExtraItems || []).map((x) => {
    if (typeof x === "string") return { label: capitalizeLetter(x) };

    return {
      ...x,
      label: capitalizeLetter(x?.label || ""),
    };
  });

  extraItemsNormalized.forEach((x) => items.push(x));

  const redirectToLink = (item) => {
    route.replace(`/employer/${item.toLowerCase()}`);
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.breadcrumb}>
        {items.map((it, index) => {
          const isLast = items.length - 1 === index;
          const label = typeof it === "string" ? it : it?.label;
          const href = typeof it === "object" ? it?.href : undefined;
          const onItemClick = typeof it === "object" ? it?.onClick : undefined;

          return (
            <React.Fragment key={`${label}-${index}`}>
              <BItem
                active={isLast}
                disabled={isLast}
                onClick={() => {
                  if (isLast) return;
                  if (onItemClick) return onItemClick();
                  if (href) return route.push(href);
                  if (label === "Candidates" || label === "Jobs") {
                    return handleRouteUpdate();
                  }
                  return redirectToLink(label);
                }}
              >
                {SelectIcons[label] && (
                  <UIIcon
                    Icon={SelectIcons[label]}
                    className={styles.icon}
                  />
                )}
                {label}
              </BItem>

              {!isLast && <RightOutlined className={styles.separator} />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};